<template>
  <div id="air-pollution" v-if="airPollutionData" class="slideshow d-flex flex-column">
    <div class="controls d-flex justify-content-center align-items-center pt-25 pb-15">
      <button class="slideshow-control previous" @click.prevent="prev">
        <i class="icon icon-slideshow-previous-button"></i>
      </button>
      <button class="slideshow-control pause" @click.prevent="pause" :disabled="!this.playState">
        <i class="icon icon-slideshow-pause-button"></i>
      </button>
      <button class="slideshow-control play" @click.prevent="play" :disabled="!!this.playState">
        <i class="icon icon-slideshow-play-button"></i>
      </button>
      <button class="slideshow-control next" @click.prevent="next">
        <i class="icon icon-slideshow-next-button"></i>
      </button>
    </div>
    <input type="range"
           id="myRange"
           class="progress mx-20"
           min="0"
           :max="loadedImagesCount() - 1"
           v-model="currentIndex"
           :style="{'--range-position': `${currentIndex / (loadedImagesCount() - 1) * 100}% 100%`}"
           @change="pause"
           @input="pause">
    <div class="render">
      <img class="current-image" :src="preloadedImages[currentIndex]" :alt="$t('omsz.map_page_title')">
      <div class="stations" v-show="firstImageLoaded">
        <span
            v-if="airPollutionData.measuringPoints"
            v-for="station in airPollutionData.measuringPoints"
            :key="station.stationId"
            class="station"
            :style="{top: `${station.percentTop}%`, left: `${station.percentLeft}%`}"
            @click.prevent.stop="getStationData(station)">
            {{ station.name }}
        </span>

        <span
            v-if="airPollutionData.cityPoints"
            v-for="area in airPollutionData.cityPoints"
            :key="area.areaId"
            class="station"
            :style="{top: `${area.percentTop}%`, left: `${area.percentLeft}%`}"
            @click.prevent.stop="$emit('area-change', area.areaId)">
                    {{ area.name }}
                </span>
      </div>
      <div v-if="chartData || isLoading"
           ref="chartModal"
           class="chart-modal"
           :class="chartModalClass"
           :style="chartModalStyle"
           v-click-outside="hideModal">
        <span v-show="isLoading">{{ $t('omsz.placeholders.please_wait') }}</span>
        <span v-if="chartError">{{ $t('omsz-form.errors.not_response') }}</span>
        <line-chart v-if="chartData" :chart-data="chartData" :width="420" :height="270"/>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ClickOutside from 'vue-click-outside'
import LineChart from '../components/LineChart';

export default {
  name: 'air-pollution',
  props: {
    airPollutionData: {
      type: Object,
      required: true,
    },
  },
  components: {
    LineChart,
  },
  directives: {
    ClickOutside,
  },
  data() {
    return {
      currentIndex: null,
      preloadedImages: {},
      delay: 1000,
      playState: null,
      isLoading: false,
      chartData: {},
      chartModalStyle: {
        opacity: 0,
      },
      chartModalClass: '',
      chartError: false,
      firstImageLoaded: false,
    };
  },
  methods: {
    preloadImages() {
      this.airPollutionData.imageUrls.forEach((url, index) => {
        this.preloadedImages[index] = url;
        let img = new Image();

        img.onload = () => {
          if (index === 0) {
            this.currentIndex = index;
            this.firstImageLoaded = true;
          }
        }

        img.src = url;
      });
    },

    loadedImagesCount() {
      return Object.keys(this.preloadedImages).length;
    },

    stepNext() {
      this.currentIndex++;
      if (this.currentIndex > this.loadedImagesCount() - 1) {
        this.currentIndex = 0;
      }
    },

    stepPrevious() {
      this.currentIndex--;
      if (this.currentIndex < 0) {
        this.currentIndex = this.loadedImagesCount() - 1;
      }
    },

    play() {
      this.stepNext();

      this.playState = setTimeout(() => {
        this.play();
      }, this.delay);
    },

    pause() {
      if (this.playState) {
        this.playState = clearTimeout(this.playState);
        this.playState = null;
      }
    },

    next() {
      this.pause();
      this.stepNext();
    },

    prev() {
      this.pause();
      this.stepPrevious();
    },

    getStationData(station) {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.chartData = null;
      this.chartError = false;

      this.chartModalStyle = {
        top: `${station.percentTop}%`,
        left: `${station.percentLeft}%`,
        opacity: 1,
      };

      this.chartModalClass = `loading ${station.percentLeft > 50 ? 'flipped-x' : ''}`;

      axios.post('/api/modelling/map-chart-data', {
        stationId: station.stationId,
        element: this.airPollutionData.element.value,
      }).then(({data}) => {
        this.chartData = data;
        this.chartModalClass = station.percentLeft > 50 ? 'flipped-x' : '';
      }).catch(error => {
        this.chartError = true;
      }).finally(() => {
        this.isLoading = false;
      });
    },

    hideModal() {
      this.chartData = null;
      this.chartModalStyle = {
        opacity: 0,
      };

      this.chartModalClass = '';
    }
  },
  mounted() {
    this.preloadImages();

    // TODO: Uses the whole component
    this.popupItem = this.$refs.chartModal;
  },
}
</script>

<style scoped lang="scss">
@import "resources/assets/fonts/icons";

.stations {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.station {
  display: inline-block;
  padding: 2px 5px;
  position: absolute;
  transform: translate(11px, calc(-100% - 3px));
  background: white;
  border-radius: 100px;
  font-size: 12px;
  line-height: 1;
  font-weight: 700;
  cursor: pointer;

  &:before {
    content: $icon-pin;
    font-family: $theme-iconfont;
    font-size: 20px;
    width: 20px;
    position: absolute;
    left: -20px;
    top: -2px;
  }
}

.chart-modal {
  position: absolute;
  background: #fff;
  border: 2px solid #12285f;
  border-radius: 10px;
  z-index: 999;
  padding: 10px;

  &:not(.loading) {
    width: 45%;
  }

  &.flipped-x {
    transform: translate(calc(-100% + 19px), 12px);

    &:before {
      right: 10px;
    }

    &:after {
      right: 13px;
    }
  }

  &:not(.flipped-x) {
    transform: translate(-21px, 12px);

    &:before {
      left: 10px;
    }

    &:after {
      left: 13px;
    }
  }

  &:before,
  &:after {
    content: "";
    position: absolute;
    border: solid rgba(0, 0, 0, 0);
  }

  &:before {
    border-width: 0 8px 12px;
    border-bottom-color: #12285f;
    top: -12px;
  }

  &:after {
    border-width: 0 5px 8px;
    border-bottom-color: #fff;
    top: -8px;
  }
}
</style>
